//src/pages/gro/QuestionnairesPage.tsx
import { useEffect, useMemo, useState } from "react";
import { FileText, Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CrudRowActions } from "@/components/shared/CrudRowActions";
import { EmptyState } from "@/components/shared/EmptyState";
import { PageHeader } from "@/components/shared/PageHeader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useQuestionnaires } from "@/hooks/useQuestionnaires";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";

type StatusFilter = "active" | "inactive" | "all";

export default function QuestionnairesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const canRead =
    user?.permissions?.includes(PERMISSIONS.QUESTIONNAIRES_READ) ?? false;
  const canWrite =
    user?.permissions?.includes(PERMISSIONS.QUESTIONNAIRES_WRITE) ?? false;

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const questionnairesQ = useQuestionnaires({
    include_inactive: statusFilter !== "active",
    q: debouncedSearch || undefined,
  });

  const items = useMemo(() => {
    const data = questionnairesQ.data ?? [];
    if (statusFilter === "inactive") {
      return data.filter((item) => !item.is_active);
    }
    return data;
  }, [questionnairesQ.data, statusFilter]);

  if (!canRead) {
    return (
      <EmptyState
        icon={FileText}
        title="Sem acesso ao módulo"
        description="Você não possui permissão para visualizar questionários."
      />
    );
  }

  return (
    <div>
      <PageHeader
        title="Questionários"
        description="Cadastre, versione e publique questionários do módulo GRO."
      >
        {canWrite ? (
          <Button onClick={() => navigate("/gro/questionnaires/novo")}>
            <Plus className="mr-2 h-4 w-4" />
            Novo questionário
          </Button>
        ) : null}
      </PageHeader>

      <Card className="p-4">
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center">
          <div className="relative w-full max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Buscar por código, nome ou descrição"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="w-full md:w-[220px]">
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as StatusFilter)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativos</SelectItem>
                <SelectItem value="inactive">Inativos</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {items.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="Nenhum questionário encontrado"
            description="Crie o primeiro questionário para começar a montar versões, seções e perguntas."
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Questionário</TableHead>
                  <TableHead>Versões</TableHead>
                  <TableHead>Publicada</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[120px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-mono text-xs">{item.code}</TableCell>
                    <TableCell>
                      <div className="font-medium">{item.name}</div>
                      {item.description ? (
                        <div className="text-sm text-muted-foreground">
                          {item.description}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell>{item.versions_count}</TableCell>
                    <TableCell>
                      {item.published_version_number ? (
                        <Badge variant="secondary">
                          v{item.published_version_number}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          Sem publicação
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={item.is_active ? "default" : "outline"}>
                        {item.is_active ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <CrudRowActions
                        onView={() => navigate(`/gro/questionnaires/${item.id}`)}
                        onEdit={
                          canWrite
                            ? () => navigate(`/gro/questionnaires/${item.id}/editar`)
                            : undefined
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
